




























































































































import Vue from 'vue';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import store from '@/store';


interface LocalSearchResult {
  entry: DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>;
  breadcrumb: string;
  endpoint: string;
  type: string;
}

@Component
export default class NavigationSearchTemplate extends Vue {
  searchQuery: string = '';
  selectedIndex: number = -1;
  localResults: LocalSearchResult[] = [];
  submenuResults: DisguiseDto.NaviSearchResultDto[] = [];
  // Pre-built flat list of all navi entries for fast client-side filtering
  private flatEntries: LocalSearchResult[] = [];

  created() {
    this.buildFlatList();
  }

  // Rebuild flat list when navi tree changes (e.g. after async reload)
  @Watch('$store.state.navi')
  onNaviUpdated() {
    this.buildFlatList();
  }

  @Watch('$store.state.naviSearch.show')
  onDialogToggled(show: boolean) {
    if (show) {
      this.searchQuery = '';
      this.localResults = [];
      this.submenuResults = [];
    }
  }

  close() {
    this.$store.commit('hideNaviSearch');
  }

  navigateTo(result: LocalSearchResult) {
    // For folders, navigate to the first navigable child (DT/FT) instead of the folder itself
    const target = result.type === 'FOLDER'
        ? this.findFirstNavigableChild(result.entry)
        : result.entry.fullPath;
    if (target) {
      this.close();
      this.$router.push(target).then(() => {
        // Force re-render navi tree so groups expand based on new route
        this.$store.commit('refreshNaviTree');
        this.$nextTick(() => {
          const activeItem = document.querySelector('.disguise-navigation-drawer .v-list-item--active');
          if (activeItem) {
            activeItem.scrollIntoView({block: 'nearest', behavior: 'smooth'});
          }
        });
      }).catch(() => { /* ignore navigation duplicated */ });
    }
  }

  resolveHref(result: LocalSearchResult): string | undefined {
    const target = result.type === 'FOLDER' ? this.findFirstNavigableChild(result.entry) : result.entry.fullPath;
    if (target) {
      return this.$router.resolve(target).href;
    }
  }

  private buildFlatList() {
    this.flatEntries = [];
    this.flattenNaviEntries(store.state.navi, []);
  }

  // Recursively flattens the navi tree into a searchable list with breadcrumb paths
  private flattenNaviEntries(
      entries: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>,
      parentNames: string[]
  ) {
    for (const entry of entries) {
      if (entry.name) {
        const naviType = entry.naviType as DisguiseDto.SimpleNaviTypeDtoUnion | undefined;
        const type = naviType?.['@type'] || '';
        const endpoint = this.extractEndpoint(naviType);

        this.flatEntries.push({
          entry,
          breadcrumb: parentNames.length > 0 ? parentNames.join(' > ') : '',
          endpoint,
          type
        });
      }
      if (entry.children && entry.children.length) {
        const newParentNames = entry.name ? [...parentNames, entry.name] : [...parentNames];
        this.flattenNaviEntries(entry.children, newParentNames);
      }
    }
  }

  onSearch() {
    if (!this.searchQuery || this.searchQuery.length < 2) {
      this.localResults = [];
      this.submenuResults = [];
      return;
    }

    this.selectedIndex = -1;
    const query = this.searchQuery.toLowerCase();
    const isSuperUser = this.$store.state.superUser;

    // Filter main navi entries: must have a path, exclude structural types for non-superusers
    this.localResults = this.flatEntries.filter((item) => {
      if (!item.entry.fullPath) {
        return false;
      }
      const naviType = (item.entry.naviType as DisguiseDto.SimpleNaviTypeDtoUnion | undefined)?.['@type'];
      if (!isSuperUser && (naviType === 'COMPOSITE' || naviType === 'QUICK_LINK')) {
        return false;
      }
      const nameMatch = item.entry.name?.toLowerCase().includes(query) || false;
      const endpointMatch = isSuperUser && item.endpoint.toLowerCase().includes(query);
      return nameMatch || endpointMatch;
    });

    // Filter pre-loaded submenu entries (not directly navigable, require context like a record)
    this.submenuResults = this.$store.state.submenuEntries.filter((entry: DisguiseDto.NaviSearchResultDto) => {
      const nameMatch = entry.name?.toLowerCase().includes(query) || false;
      const endpointMatch = isSuperUser && (entry.endpoint?.toLowerCase().includes(query) || false);
      return nameMatch || endpointMatch;
    });
  }

  private extractEndpoint(naviType: DisguiseDto.SimpleNaviTypeDtoUnion | undefined): string {
    if (!naviType) {
      return '';
    }
    const type = naviType['@type'];
    if (type === 'DATATABLE' || type === 'FORMULAR' || type === 'STRUCTURE') {
      return (naviType as any).templateUrl || '';
    }
    if (type === 'TIMELINEV2') {
      return (naviType as any).endpointUrl || '';
    }
    return '';
  }

  // Finds the first navigable child entry (DT, FT, etc.) within a folder hierarchy.
  // Used when a normal user clicks on a folder search result.
  private findFirstNavigableChild(entry: DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>): string | undefined {
    if (!entry.children) {
      return undefined;
    }
    for (const child of entry.children) {
      const type = (child.naviType as DisguiseDto.SimpleNaviTypeDtoUnion | undefined)?.['@type'];
      if (type !== 'FOLDER' && type !== 'COMPOSITE' && type !== 'QUICK_LINK' && child.fullPath) {
        return child.fullPath;
      }
      const found = this.findFirstNavigableChild(child);
      if (found) {
        return found;
      }
    }
    return undefined;
  }

  // Translates navi type names to user-facing German labels (only for types shown to normal users)
  private translateNaviType(type: string | undefined): string {
    if (type === 'DATATABLE') {
      return 'Tabelle';
    }
    if (type === 'FORMULAR') {
      return 'Formular';
    }
    if (type === 'FOLDER') {
      return 'Bereich';
    }
    if (type === 'TIMELINE' || type === 'TIMELINEV2') {
      return 'Timeline';
    }
    return type || '';
  }
}
